import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

import { shouldBlockPatientChromeLeave } from "./shouldBlockPatientChromeLeave";

export interface UseLeaveRecordingGuardResult {
  isLeaveDialogOpen: boolean;
  requestLeave: (onConfirmLeave: () => void) => void;
  keepRecording: () => void;
  /**
   * Call before intentional navigation away (Finish → visit) so the blocker
   * does not trap the route change. Uses a ref so the bypass is synchronous.
   */
  allowNextNavigation: () => void;
}

/**
 * Blocks leaving patient chrome while recording is live, and exposes dialog
 * state for Keep recording. Agenda ↔ Past Visits never blocks.
 */
export function useLeaveRecordingGuard(input: {
  isRecordingLive: boolean;
}): UseLeaveRecordingGuardResult {
  const [manualLeave, setManualLeave] = useState<(() => void) | null>(null);
  const allowLeaveRef = useRef(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowLeaveRef.current) return false;
    return shouldBlockPatientChromeLeave({
      isRecordingLive: input.isRecordingLive,
      currentPath: currentLocation.pathname,
      nextPath: nextLocation.pathname,
    });
  });

  const isLeaveDialogOpen = blocker.state === "blocked" || manualLeave != null;

  const keepRecording = useCallback(() => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setManualLeave(null);
  }, [blocker]);

  const allowNextNavigation = useCallback(() => {
    allowLeaveRef.current = true;
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setManualLeave(null);
  }, [blocker]);

  const requestLeave = useCallback(
    (onConfirmLeave: () => void) => {
      if (!input.isRecordingLive) {
        onConfirmLeave();
        return;
      }
      setManualLeave(() => onConfirmLeave);
    },
    [input.isRecordingLive],
  );

  useEffect(() => {
    if (!input.isRecordingLive) {
      allowLeaveRef.current = false;
      setManualLeave(null);
    }
  }, [input.isRecordingLive]);

  return {
    isLeaveDialogOpen,
    requestLeave,
    keepRecording,
    allowNextNavigation,
  };
}
